import type { EventBinding } from "../api/events";
import { eventBindingKey, type EventStore } from "./event-operation-store";

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KEY = /^bid:[a-z0-9]+:[0-9a-f]{36}$/;
export const BID_OPERATIONS = [
  "supplier.bid.start",
  "supplier.bid.save",
  "supplier.bid.adopt",
  "supplier.bid.revise",
  "supplier.bid.submit",
  "supplier.bid.withdraw",
  "supplier.bid.decline",
  "supplier.bid.resume",
  "supplier.bid.document.prepare",
  "supplier.bid.document.freeze",
  "supplier.bid.document.attach",
  "supplier.bid.document.detach",
  "sourcing.close",
] as const;
export type BidOperationType = (typeof BID_OPERATIONS)[number];
export interface BidScope {
  eventId: string;
  revisionId: string;
  participationId?: string;
  grantId?: string;
  bidId?: string;
  documentId?: string;
}
export interface BidOperation extends BidScope {
  operationType: BidOperationType;
  idempotencyKey: string;
  startedAt: number;
}
export function bidBindingKey(binding: EventBinding) {
  if (!["supplier", "workspace"].includes(binding.audience))
    throw new Error("Invalid bid audience");
  return eventBindingKey(binding).replace(".phase5.", ".phase6.");
}
export const bidIntent = (scope: BidScope) =>
  `${scope.eventId}:${scope.participationId ?? "event"}:${scope.grantId ?? "buyer"}`;

function project(value: unknown, binding: EventBinding): BidOperation {
  const row = value as Partial<BidOperation> | null;
  if (
    !row ||
    !UUID.test(row.eventId ?? "") ||
    !UUID.test(row.revisionId ?? "") ||
    !KEY.test(row.idempotencyKey ?? "") ||
    !BID_OPERATIONS.includes(row.operationType as BidOperationType) ||
    (binding.audience === "supplier") !==
      row.operationType?.startsWith("supplier.bid.") ||
    (binding.audience === "supplier" &&
      (!row.participationId || !row.grantId)) ||
    (binding.audience === "workspace" &&
      [row.participationId, row.grantId, row.bidId, row.documentId].some(
        (value) => value !== undefined,
      )) ||
    [row.participationId, row.grantId, row.bidId, row.documentId].some(
      (id) => id !== undefined && !UUID.test(id),
    ) ||
    typeof row.startedAt !== "number" ||
    !Number.isFinite(row.startedAt) ||
    row.startedAt < 0
  )
    throw new Error("Invalid bid recovery identity");
  return {
    eventId: row.eventId!,
    revisionId: row.revisionId!,
    participationId: row.participationId,
    grantId: row.grantId,
    bidId: row.bidId,
    documentId: row.documentId,
    operationType: row.operationType!,
    idempotencyKey: row.idempotencyKey!,
    startedAt: row.startedAt,
  };
}
export function restoreBidOperations(store: EventStore, binding: EventBinding) {
  const prefix = bidBindingKey(binding),
    records: BidOperation[] = [];
  for (let index = 0; index < store.length; index++) {
    const key = store.key(index);
    if (!key?.startsWith(prefix)) continue;
    const record = project(JSON.parse(store.getItem(key) ?? "null"), binding);
    if (key !== prefix + record.idempotencyKey)
      throw new Error("Mismatched bid recovery key");
    records.push(record);
  }
  if (records.length > 12) throw new Error("Bid recovery capacity exceeded");
  return records.sort((a, b) => a.startedAt - b.startedAt);
}
export function persistBidOperation(
  store: EventStore,
  binding: EventBinding,
  value: BidOperation,
) {
  const record = project(value, binding),
    records = restoreBidOperations(store, binding);
  if (
    records.length >= 12 &&
    !records.some((x) => x.idempotencyKey === record.idempotencyKey)
  )
    throw new Error("Bid recovery capacity exceeded");
  const key = bidBindingKey(binding) + record.idempotencyKey,
    encoded = JSON.stringify(record);
  store.setItem(key, encoded);
  if (store.getItem(key) !== encoded)
    throw new Error("Bid recovery storage unavailable");
}
export function removeBidOperation(
  store: EventStore,
  binding: EventBinding,
  key: string,
) {
  if (!KEY.test(key)) throw new Error("Invalid bid command key");
  store.removeItem(bidBindingKey(binding) + key);
}
export function clearBidOperations(store: EventStore, binding: EventBinding) {
  const prefix = bidBindingKey(binding);
  Array.from({ length: store.length }, (_, index) => store.key(index))
    .filter((key): key is string => Boolean(key?.startsWith(prefix)))
    .forEach((key) => store.removeItem(key));
}

// Deliberately validate nested resource identity, rather than treating a receipt/file as an event ID.
export function bidCompletion(value: unknown, operation: BidOperation) {
  if (!value || typeof value !== "object") return false;
  const data = value as Record<string, unknown>;
  const field = (name: string) =>
    data[name] ?? data[name[0].toUpperCase() + name.slice(1)];
  const uuid = (name: string) =>
    typeof field(name) === "string" && UUID.test(field(name) as string);
  if (
    field("eventId") !== operation.eventId ||
    field("revisionId") !== operation.revisionId ||
    !/^[1-9][0-9]*$/.test(String(field("version")))
  )
    return false;
  if (operation.operationType === "sourcing.close")
    return (
      uuid("closureId") &&
      field("windowId") === operation.revisionId &&
      typeof field("closedAt") === "string"
    );
  if (
    field("participationId") !== operation.participationId ||
    !uuid("bidId") ||
    !uuid("resourceId") ||
    (operation.bidId && field("bidId") !== operation.bidId) ||
    (operation.documentId && field("resourceId") !== operation.documentId)
  )
    return false;
  if (operation.operationType === "supplier.bid.submit")
    return (
      uuid("receiptId") &&
      field("receiptId") === field("resourceId") &&
      field("state") === "Submitted" &&
      /^[1-9][0-9]*$/.test(String(field("submissionNumber")))
    );
  const state =
    (
      {
        "supplier.bid.withdraw": "Withdrawn",
        "supplier.bid.decline": "NoBid",
        "supplier.bid.resume": "Resumed",
      } as Partial<Record<BidOperationType, string>>
    )[operation.operationType] ?? "Draft";
  return field("state") === state;
}
